import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';

// Template brand ID that contains shared prompts for all brands
const TEMPLATE_BRAND_ID = '4fc6b2bd-dd18-4b38-9954-72a011af3313';

interface PromptPreview {
  id: string;
  name: string;
  prompt: string;
  preview: string;
  type: string;
  isTemplate?: boolean;
}

export function usePromptPreviews() {
  const [prompts, setPrompts] = useState<PromptPreview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentBrandId } = useBrandManager();

  const fetchPromptPreviews = async () => {
    if (!currentBrandId) return;

    try {
      setIsLoading(true);
      setError(null);

      // If we're viewing the template brand, only fetch template prompts once
      if (currentBrandId === TEMPLATE_BRAND_ID) {
        const templateResponse = await supabase
          .from('os_art_prompts')
          .select('id, name, prompt, preview, type')
          .eq('brand_id', TEMPLATE_BRAND_ID)
          .order('created_at', { ascending: false });

        if (templateResponse.error) throw templateResponse.error;

        const templatePreviews = (templateResponse.data || []).map(preview => ({
          ...preview,
          isTemplate: true
        })) as PromptPreview[];

        setPrompts(templatePreviews);
      } else {
        // For other brands, fetch both template and brand prompts
        const [templateResponse, brandResponse] = await Promise.all([
          supabase
            .from('os_art_prompts')
            .select('id, name, prompt, preview, type')
            .eq('brand_id', TEMPLATE_BRAND_ID)
            .order('created_at', { ascending: false }),
          supabase
            .from('os_art_prompts')
            .select('id, name, prompt, preview, type')
            .eq('brand_id', currentBrandId)
            .order('created_at', { ascending: false })
        ]);

        if (templateResponse.error) throw templateResponse.error;
        if (brandResponse.error) throw brandResponse.error;

        // Mark template previews and combine with brand previews
        const templatePreviews = (templateResponse.data || []).map(preview => ({
          ...preview,
          isTemplate: true
        })) as PromptPreview[];

        const brandPreviews = (brandResponse.data || []).map(preview => ({
          ...preview,
          isTemplate: false
        })) as PromptPreview[];

        // Combine and dedupe by id (prefer brand prompts over template)
        const allPreviews = [...templatePreviews];
        const templateIds = new Set(templatePreviews.map(p => p.id));
        
        brandPreviews.forEach(brandPreview => {
          if (!templateIds.has(brandPreview.id)) {
            allPreviews.push(brandPreview);
          }
        });

        setPrompts(allPreviews);
      }
    } catch (err) {
      console.error('Error fetching prompt previews:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch prompt previews');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPromptPreviews();
  }, [currentBrandId]);

  return {
    prompts,
    isLoading,
    error,
    refetch: fetchPromptPreviews
  };
}